import { useEffect, useId, useRef, useState } from 'react';
import type { Presentation } from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { loadSettings, saveSettings } from '../lib/settings.ts';
import { SectionHeader } from './ui.tsx';

const OPTIONS: readonly { value: Presentation; label: MessageKey; hint: MessageKey }[] = [
  { value: 'auto', label: 'settings.presentation.auto', hint: 'settings.presentation.auto.hint' },
  { value: 'text', label: 'settings.presentation.text', hint: 'settings.presentation.text.hint' },
  { value: 'visual', label: 'settings.presentation.visual', hint: 'settings.presentation.visual.hint' },
];

/** Saves each choice as it is made; a failed save puts the last saved choice back. */
export function PresentationSetting() {
  const [value, setValue] = useState<Presentation>('auto');
  const [failed, setFailed] = useState(false);
  const saved = useRef<Presentation>('auto');
  const latest = useRef(0);
  const touched = useRef(false);
  const id = useId();

  useEffect(() => {
    let live = true;
    loadSettings().then(
      (settings) => {
        if (!live || touched.current) return;
        saved.current = settings.presentation;
        setValue(settings.presentation);
      },
      () => {},
    );
    return () => { live = false; };
  }, []);

  const choose = async (next: Presentation) => {
    touched.current = true;
    const request = ++latest.current;
    setValue(next);
    setFailed(false);
    try {
      const settings = await saveSettings({ presentation: next });
      saved.current = settings.presentation;
    } catch {
      if (request !== latest.current) return;
      setValue(saved.current);
      setFailed(true);
    }
  };

  return (
    <section className="card card--pad-lg stack-md" aria-labelledby={id}>
      <SectionHeader title={t('settings.presentation.title')} id={id} />
      <p className="muted-2">{t('settings.presentation.note')}</p>
      <div className="choice-list" role="radiogroup" aria-labelledby={id}>
        {OPTIONS.map((option) => (
          <label key={option.value} className="choice">
            <input
              type="radio"
              name={`${id}-presentation`}
              value={option.value}
              checked={value === option.value}
              aria-describedby={`${id}-${option.value}`}
              onChange={() => void choose(option.value)}
            />
            <span className="choice__text">
              <span className="choice__name">{t(option.label)}</span>
              <span className="choice__hint" id={`${id}-${option.value}`}>{t(option.hint)}</span>
            </span>
          </label>
        ))}
      </div>
      <p className="choice-status" role="status" aria-live="polite">{failed ? t('settings.presentation.error') : ''}</p>
    </section>
  );
}
